import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Divider, Grid, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { GetPolicyWalletEligibleUsers } from "../../Service/_index";
import AddMoneyForm from "./AddMoneyForm";
import RecentAdditions from "./RecentAdditions";
import { canAddMoneyToUsers, isSuperAdminLogin } from "./addMoneyUtils";
import { StatePanel } from "./parts";

const PageFrame = ({ children }) => {
  const navigate = useNavigate();
  return (
    <Grid container className="Master_Card_Container">
      <Grid container alignItems="center" className="Master_Card_Sub_Container">
        <Grid item xs={12} sm={7} md={8}>
          <Typography className="Master_Card_Tittle">Add Money to User Wallet</Typography>
          <Typography sx={{ color: "#667085", fontSize: "12.5px", fontFamily: "Inter" }}>
            Credit a user&apos;s Policy Wallet directly, for example after receiving cash or a bank transfer
          </Typography>
        </Grid>
        <Grid item xs={12} sm={5} md={4} className="d-flex justify-content-end" sx={{ mt: { xs: 1.5, sm: 0 } }}>
          <Button
            className="Master_Card_Filter_Button"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/adminPolicyWallet")}
          >
            Back
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
      </Grid>
      <Grid item xs={12}>
        {children}
      </Grid>
    </Grid>
  );
};

const AddMoneyToUserPage = () => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");
  const [historyKey, setHistoryKey] = useState(0);
  // The super admin: every admin's users, narrowed by an optional Admin filter.
  const superAdmin = isSuperAdminLogin();
  const [admins, setAdmins] = useState([]);
  const [adminFilter, setAdminFilter] = useState(null);
  const latestRequest = useRef(0);

  const loadUsers = useCallback(() => {
    const id = ++latestRequest.current;
    setUsersLoading(true);
    setUsersError("");
    GetPolicyWalletEligibleUsers(superAdmin && adminFilter ? { adminId: adminFilter._id } : undefined).then((res) => {
      if (id !== latestRequest.current) return;
      setUsersLoading(false);
      if (res.error) {
        setUsersError(res.message || "Could not load users");
        return;
      }
      setUsers(Array.isArray(res.data) ? res.data : []);
      if (Array.isArray(res.admins)) setAdmins(res.admins);
    });
  }, [superAdmin, adminFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // After a credit the balances in the picker and the history list are both stale.
  const handleCredited = () => {
    loadUsers();
    setHistoryKey((n) => n + 1);
  };

  return (
    <PageFrame>
      <AddMoneyForm
        users={users}
        usersLoading={usersLoading}
        usersError={usersError}
        onRetryUsers={loadUsers}
        onCredited={handleCredited}
        superAdmin={superAdmin}
        admins={admins}
        adminFilter={adminFilter}
        onAdminFilter={setAdminFilter}
      />
      <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
      <RecentAdditions users={users} usersLoading={usersLoading} refreshKey={historyKey} />
    </PageFrame>
  );
};

// Accountants share the admin menu but must not credit wallets; the menu hides
// the link and this keeps a typed-in URL calm instead of erroring. The
// backend enforces the same rule.
const AddMoneyToUser = () =>
  canAddMoneyToUsers() ? (
    <AddMoneyToUserPage />
  ) : (
    <PageFrame>
      <StatePanel
        icon={<LockOutlinedIcon />}
        title="This page is restricted"
        text="Adding money to user wallets is available to the account owner (admin) only."
      />
    </PageFrame>
  );

export default AddMoneyToUser;
